import React, { useState, useEffect } from "react"
import { Form, Button, Row, Col, Spinner, Card, Modal } from "react-bootstrap"
import { useParams, useNavigate } from "react-router-dom"
import { useAuth } from "../context/AuthContext"
import useAuthAxios from "../hooks/useAuthAxios"
import Layout from "../components/layout/Layout"
import { toast } from "react-toastify"
import BackButton from "../components/common/BackButton"
import FileUploadModal from "../components/repairs/FileUploadModal"

const GarageDetails = () => {
  const { garageId: paramGarageId } = useParams()
  const navigate = useNavigate()
  const { user, isAdmin } = useAuth()
  const { get, put, post, del } = useAuthAxios()

  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [garageData, setGarageData] = useState(null)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [showImageModal, setShowImageModal] = useState(false)
  const [modalImageSrc, setModalImageSrc] = useState("")
  const [modalImageAlt, setModalImageAlt] = useState("")
  const [modalImageLoading, setModalImageLoading] = useState(false)

  // Bank accounts state for editing
  const [bankAccounts, setBankAccounts] = useState([])

  useEffect(() => {
    const fetchGarageData = async () => {
      if (!isAdmin()) {
        toast.error("Δεν έχετε δικαίωμα πρόσβασης.")
        navigate("/")
        return
      }
      setIsLoading(true)
      try {
        const response = await get(`/garages/${paramGarageId}`)
        if (response.success && response.data) {
          setGarageData(response.data)
          setBankAccounts(response.data.BankAccounts?.GarageBankAccount || [])
        } else {
          toast.error(response.data?.Errors?.Problem || "Το συνεργείο δεν βρέθηκε")
        }
      } catch (err) {
        toast.error("Σφάλμα φόρτωσης δεδομένων συνεργείου:", err)
      } finally {
        setIsLoading(false)
      }
    }

    if (paramGarageId) {
      fetchGarageData()
    }
  }, [paramGarageId, get, navigate, isAdmin])

  useEffect(() => {
    const fetchProfileGarageData = async () => {
      if (!user) return

      const garageId = user?.Garage?.GarageID
      if (!garageId) {
        toast.info("Δεν έχετε συνδεδεμένο συνεργείο.")
        navigate("/")
        return
      }

      setIsLoading(true)
      try {
        const response = await get(`/garages/${garageId}`)
        if (response.success && response.data) {
          setGarageData(response.data)
          setBankAccounts(response.data.BankAccounts?.GarageBankAccount || [])
        } else {
          toast.error(response.data?.Errors?.Problem || "Το συνεργείο δεν βρέθηκε")
          setGarageData(null)
          setBankAccounts([])
        }
      } catch (err) {
        toast.error("Σφάλμα φόρτωσης δεδομένων συνεργείου.")
        setGarageData(null)
        setBankAccounts([])
      } finally {
        setIsLoading(false)
      }
    }

    if (!paramGarageId) {
      fetchProfileGarageData()
    }
  }, [paramGarageId, user, navigate, get])

  const handleInputChange = (nameOrEvent, value) => {
    if (nameOrEvent.target) {
      const { name, value: eventValue, type, checked } = nameOrEvent.target
      setGarageData(prev => ({
        ...prev,
        [name]: type === "checkbox" ? checked : eventValue
      }))
    } else {
      setGarageData(prev => ({
        ...prev,
        [nameOrEvent]: value
      }))
    }
  }

  // Supervisor change handler
  const handleSupervisorChange = e => {
    const { name, value } = e.target
    setGarageData(prev => ({
      ...prev,
      Supervisor: {
        ...prev.Supervisor,
        [name]: value
      }
    }))
  }

  // Bank accounts handlers
  const handleBankAccountChange = (idx, field, value) => {
    setBankAccounts(prev => prev.map((acc, i) => (i === idx ? { ...acc, [field]: value } : acc)))
  }
  const handleAddBankAccount = () => {
    setBankAccounts(prev => [...prev, { Bank: "", IBAN: "" }])
  }
  const handleDeleteBankAccount = idx => {
    setBankAccounts(prev => prev.filter((_, i) => i !== idx))
  }

  // Update garageData.BankAccounts before submit
  const handleSubmit = async e => {
    e.preventDefault()
    setIsSubmitting(true)
    try {
      const dataToSend = {
        ...garageData,
        BankAccounts: { GarageBankAccount: bankAccounts }
      }
      const response = await put(`/garages/${garageData.GarageID}`, dataToSend)
      if (response.success && response.data.IsSuccess) {
        toast.success("Τα στοιχεία του συνεργείου ενημερώθηκαν επιτυχώς")

        // Refresh data to get the latest version
        const refreshResponse = await get(`/garages/${garageData.GarageID}`)
        if (refreshResponse.success && refreshResponse.data) {
          setGarageData(refreshResponse.data)
        }
      } else {
        toast.error(
          response.data?.Problem ||
            response.problem ||
            "Παρουσιάστηκε πρόβλημα κατά την ενημέρωση του Συνεργείου. Παρακαλώ δοκιμάστε αργότερα"
        )
      }
    } catch (error) {
      console.error("Update garage error:", error)
      toast.error(
        error.response?.data?.Problem ||
          "Αποτυχία ενημέρωσης συνεργείου. Ελέγξτε τη σύνδεσή σας ή δοκιμάστε ξανά."
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleFileUpload = async file => {
    const photos = [
      {
        fileName: file.fileName,
        fileContent: file.fileContent
      }
    ]
    try {
      const response = await post(`/garages/${garageData.GarageID}/photos`, { photos })
      if (response.success) {
        if (response.data && response.data.IsSuccess === false) {
          toast.error(response.data.Problem || "Αποτυχία ανεβάσματος αρχείου.")
          return
        }
        toast.success("Οι φωτογραφίες ανέβηκαν επιτυχώς")
        // Refresh data
        const refreshResponse = await get(`/garages/${garageData.GarageID}`)
        if (refreshResponse.success && refreshResponse.data) {
          setGarageData(refreshResponse.data)
        }
        setShowUploadModal(false)
      }
    } catch (error) {
      toast.error("Σφάλμα κατά το ανέβασμα των φωτογραφιών.")
      setShowUploadModal(false)
    }
  }

  const handleDeletePhoto = async fileId => {
    try {
      const response = await del(`/garages/${garageData.GarageID}/photos/${fileId}`)
      if (response.success) {
        toast.success("Η φωτογραφία διαγράφηκε επιτυχώς")
        setGarageData(prev => ({
          ...prev,
          Photos: {
            ...prev.Photos,
            GaragePhotos: prev.Photos.GaragePhotos.filter(p => p.FileID !== fileId)
          }
        }))
      }
    } catch (error) {
      console.log(error)
      toast.error("Σφάλμα κατά τη διαγραφή της φωτογραφίας.")
    }
  }

  // Image modal logic
  const handleImageClick = async photo => {
    setModalImageLoading(true)
    setShowImageModal(true)
    setModalImageAlt(photo.FileName)
    setModalImageSrc("")
    try {
      const response = await get(`/garages/${garageData.GarageID}/photos/${photo.FileID}/${photo.FileName}`, {
        responseType: "blob"
      })
      const blob = response.data instanceof Blob ? response.data : new Blob([response.data])
      const url = URL.createObjectURL(blob)
      setModalImageSrc(url)
    } catch (err) {
      setModalImageSrc("")
      toast.error("Αποτυχία φόρτωσης εικόνας.")
    } finally {
      setModalImageLoading(false)
    }
  }

  if (isLoading) {
    return (
      <Layout>
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
        </div>
      </Layout>
    )
  }

  if (!garageData) {
    return <Layout />
  }

  return (
    <Layout>
      <div className="content-glass mb-4 position-relative">
        <BackButton className="mb-3" />

        <div className="d-flex align-items-center mb-4 ">
          <div className="d-flex align-items-center">
            <i className="fas fa-wrench me-2" />
            <h5 className="mb-0">Στοιχεία Συνεργείου</h5>
          </div>
        </div>
        <Form onSubmit={handleSubmit}>
          <Row className="g-3">
            <Col md={12}>
              <Form.Group>
                <Form.Check
                  type="checkbox"
                  name="Active"
                  label="Ενεργό"
                  checked={!!garageData.Active}
                  onChange={e => handleInputChange("Active", e.target.checked ? 1 : 0)}
                  disabled={isSubmitting}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Κωδικός Συνεργείου</Form.Label>
                <Form.Control type="text" name="GarageID" value={garageData.GarageID} disabled />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Επωνυμία</Form.Label>
                <Form.Control
                  type="text"
                  name="Eponimia"
                  value={garageData.Eponimia}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Ιστοσελίδα</Form.Label>
                <Form.Control
                  type="text"
                  name="Website"
                  value={garageData.Website}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Οδός</Form.Label>
                <Form.Control
                  type="text"
                  name="AddressStreet"
                  value={garageData.AddressStreet}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Αριθμός</Form.Label>
                <Form.Control
                  type="text"
                  name="AddressStreetNo"
                  value={garageData.AddressStreetNo}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Πόλη</Form.Label>
                <Form.Control
                  type="text"
                  name="AddressCity"
                  value={garageData.AddressCity}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Τ.Κ.</Form.Label>
                <Form.Control
                  type="text"
                  name="AddressPostalCode"
                  value={garageData.AddressPostalCode}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Τηλέφωνο 1</Form.Label>
                <Form.Control
                  type="text"
                  name="Phone1"
                  value={garageData.Phone1}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Τηλέφωνο 2</Form.Label>
                <Form.Control
                  type="text"
                  name="Phone2"
                  value={garageData.Phone2}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Τηλέφωνο 3</Form.Label>
                <Form.Control
                  type="text"
                  name="Phone3"
                  value={garageData.Phone3}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                />
              </Form.Group>
            </Col>
          </Row>

          <hr />

          {/* Supervisor Section */}
          <div className="mt-4">
            <div className="d-flex align-items-center mb-4">
              <i className="fas fa-user-tie me-2" />
              <h5 className="mb-0">Υπεύθυνος Συνεργείου</h5>
            </div>
            <Row className="g-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Όνομα</Form.Label>
                  <Form.Control
                    type="text"
                    name="Firstname"
                    value={garageData.Supervisor.Firstname}
                    onChange={handleSupervisorChange}
                    disabled={isSubmitting}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Επώνυμο</Form.Label>
                  <Form.Control
                    type="text"
                    name="LastName"
                    value={garageData.Supervisor.LastName}
                    onChange={handleSupervisorChange}
                    disabled={isSubmitting}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="Mail"
                    value={garageData.Supervisor.Mail}
                    onChange={handleSupervisorChange}
                    disabled={isSubmitting}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Τηλέφωνο</Form.Label>
                  <Form.Control
                    type="text"
                    name="Phone"
                    value={garageData.Supervisor.Phone}
                    onChange={handleSupervisorChange}
                    disabled={isSubmitting}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Κινητό</Form.Label>
                  <Form.Control
                    type="text"
                    name="Mobile"
                    value={garageData.Supervisor.Mobile}
                    onChange={handleSupervisorChange}
                    disabled={isSubmitting}
                  />
                </Form.Group>
              </Col>
            </Row>
          </div>

          <hr />

          {/* Οικονομικά Στοιχεία Section */}
          <div className="mt-4">
            <div className="d-flex align-items-center mb-4">
              <i className="fas fa-euro-sign me-2" />
              <h5 className="mb-0">Οικονομικά Στοιχεία</h5>
            </div>
            <Row className="g-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>ΑΦΜ</Form.Label>
                  <Form.Control
                    type="text"
                    name="AFM"
                    value={garageData.AFM}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>ΔΟΥ</Form.Label>
                  <Form.Control
                    type="text"
                    name="DOY"
                    value={garageData.DOY}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>ΓΕΜΗ</Form.Label>
                  <Form.Control
                    type="text"
                    name="GEMH"
                    value={garageData.GEMH}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                  />
                </Form.Group>
              </Col>
            </Row>
          </div>

          <hr />

          {/* Editable Bank Accounts */}
          <div className="mt-4">
            <div className="d-flex align-items-center mb-2">
              <i className="fas fa-university me-2" />
              <h5 className="mb-0">Τραπεζικοί Λογαριασμοί</h5>
              <Button
                variant="success"
                size="sm"
                className="ms-3"
                onClick={handleAddBankAccount}
                disabled={isSubmitting}>
                <i className="fas fa-plus me-1" /> Προσθήκη
              </Button>
            </div>
            <table className="table table-bordered">
              <thead>
                <tr>
                  <th>Τράπεζα</th>
                  <th>IBAN</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {bankAccounts.map((acc, idx) => (
                  <tr key={idx}>
                    <td>
                      <Form.Control
                        type="text"
                        value={acc.Bank}
                        onChange={e => handleBankAccountChange(idx, "Bank", e.target.value)}
                        disabled={isSubmitting}
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        value={acc.IBAN}
                        onChange={e => handleBankAccountChange(idx, "IBAN", e.target.value)}
                        disabled={isSubmitting}
                      />
                    </td>
                    <td className="text-center">
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleDeleteBankAccount(idx)}
                        disabled={isSubmitting}>
                        <i className="fas fa-trash" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <hr />
          <div className="d-flex justify-content-end mt-4">
            <Button type="submit" variant="primary" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Αποθήκευση...
                </>
              ) : (
                <>
                  <i className={`fas fa-save me-2`} />
                  Αποθήκευση
                </>
              )}
            </Button>
          </div>
        </Form>
      </div>

      {/* Photos Section (copied and adapted from RepairDetails.jsx) */}
      <div className="col-12" data-container-query>
        <Card>
          <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div className="d-flex align-items-center">
              <i className="fas fa-camera me-2" />
              <h6 className="mb-0">Φωτογραφίες</h6>
            </div>
            <div className="d-flex gap-2">
              <Button
                variant="primary"
                size="sm"
                onClick={() => setShowUploadModal(true)}
                disabled={isSubmitting}>
                <i className="fas fa-upload me-2" />
                Ανέβασμα Αρχείου
              </Button>
              {garageData.Photos?.GaragePhotos?.length > 0 && (
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={async () => {
                    // Download all photos as zip
                    const JSZip = (await import("jszip")).default
                    const zip = new JSZip()
                    const files = garageData.Photos.GaragePhotos
                    for (const file of files) {
                      try {
                        const response = await get(
                          `/garages/${garageData.GarageID}/photos/${file.FileID}/${file.FileName}`,
                          { responseType: "blob" }
                        )
                        zip.file(file.FileName, response.data)
                      } catch (error) {
                        toast.error(`Αποτυχία λήψης αρχείου: ${file.FileName}`)
                      }
                    }
                    const content = await zip.generateAsync({ type: "blob" })
                    const url = window.URL.createObjectURL(content)
                    const link = document.createElement("a")
                    link.href = url
                    link.download = `Φωτογραφίες_Συνεργείου_${garageData.GarageID}.zip`
                    document.body.appendChild(link)
                    link.click()
                    document.body.removeChild(link)
                    window.URL.revokeObjectURL(url)
                    toast.success("Η λήψη των αρχείων ολοκληρώθηκε", { autoClose: 3000 })
                  }}
                  disabled={isSubmitting}>
                  <i className="fas fa-download me-2" />
                  Λήψη Όλων
                </Button>
              )}
            </div>
          </Card.Header>
          <Card.Body>
            {garageData.Photos?.GaragePhotos?.length ? (
              <div className="d-flex flex-wrap gap-4 justify-content-center justify-content-container-md-start">
                {garageData.Photos.GaragePhotos.map(photo => (
                  <div
                    key={photo.FileID}
                    className="position-relative"
                    style={{ width: "150px", cursor: "pointer" }}>
                    <div
                      className="file-preview-container border rounded p-2 text-center"
                      onClick={() => handleImageClick(photo)}
                      style={{
                        height: "150px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#f8f9fa",
                        transition: "all 0.2s ease-in-out"
                      }}>
                      <img
                        src={`data:image/jpeg;base64,${photo.FileByte}`}
                        alt={photo.FileName}
                        style={{
                          maxWidth: "100%",
                          maxHeight: "100%",
                          objectFit: "contain"
                        }}
                      />
                      <div
                        className="file-actions position-absolute top-0 end-0 m-2 opacity-0"
                        style={{ transition: "opacity 0.2s ease-in-out" }}>
                        <Button
                          variant="light"
                          className="shadow-sm"
                          size="sm"
                          onClick={e => {
                            e.stopPropagation()
                            handleImageClick(photo)
                          }}>
                          <i className="fas fa-eye" />
                        </Button>
                        <Button
                          variant="light"
                          className="shadow-sm"
                          size="sm"
                          onClick={e => {
                            e.stopPropagation()
                            handleDeletePhoto(photo.FileID)
                          }}>
                          <i className="fas fa-trash text-danger" />
                        </Button>
                      </div>
                    </div>
                    <div className="text-center mt-2">
                      <small className="d-block text-truncate" style={{ maxWidth: "150px" }}>
                        {photo.FileName}
                      </small>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4 text-muted">
                <i className="fas fa-file fa-3x mb-3" />
                <div>Δεν υπάρχουν διαθέσιμες φωτογραφίες</div>
              </div>
            )}
          </Card.Body>
        </Card>
      </div>
      <style>
        {`
          [data-container-query] {
            container-type: inline-size;
            container-name: responsive-container;
          }
          @container responsive-container (min-width: 768px) {
            .col-container-md-6 {
              width: 50%;
            }
            .col-container-md-3 {
              width: 25%;
            }
            .flex-container-md-row {
              flex-direction: row !important;
            }
            .justify-content-container-md-start {
              justify-content: flex-start !important;
            }
          }
          .file-preview-container:hover {
            background-color: #e9ecef !important;
          }
          .file-preview-container:hover .file-actions {
            opacity: 1 !important;
          }
        `}
      </style>

      {/* Image Modal */}
      <Modal show={showImageModal} onHide={() => setShowImageModal(false)} size="lg" centered>
        <Modal.Body className="text-center">
          {modalImageLoading ? (
            <Spinner animation="border" variant="primary" />
          ) : modalImageSrc ? (
            <img src={modalImageSrc} alt={modalImageAlt} style={{ maxWidth: "100%", maxHeight: "80vh" }} />
          ) : (
            <div className="text-danger">Αποτυχία φόρτωσης εικόνας.</div>
          )}
        </Modal.Body>
      </Modal>

      <FileUploadModal
        show={showUploadModal}
        onHide={() => setShowUploadModal(false)}
        onUpload={handleFileUpload}
      />
    </Layout>
  )
}

export default GarageDetails
